import React, { useEffect, useRef, useState } from 'react';
import ActivityCenter from './ActivityCenter';
import Overview from './Overview';
import QuickView from './QuickView';
import './Dashboard.css';
import DashBoardHeader from './ActivityCenter/DashboardHeader/DashBoardHeader';
import CreateWidget from './DashBoardBody/CreateWidget/CreateWidget';
import DashBoardBody from './DashBoardBody/DashBoardBody';
import ConfigureLayout from './DashBoardBody/ConfigureLayout';
import CreateDashBoard from './DashBoardBody/CreateDashBoard';
import DashBoardService from '../../../services/DashBoardService/DashBoardService';
import { id } from 'date-fns/locale';
import NewDeleteConfirm from '../../../components/DataTable/NewDeleteConfirm';
import { useTranslation } from 'react-i18next';
import { groupableHorizontalChartOptions, groupableVerticalChartOptions, horizontalChartOptions, pieChartOptions, verticalChartOptions } from './DashBoardBody/Widgets/WidgetOptions';
import { alertState } from '../../../recoil-store/recoil-state';
import { useSetRecoilState } from 'recoil';
import { Chart } from 'chart.js';

const dynamicLegendHeightPlugin = {
    id: 'dynamicLegendHeight',
    afterRender: function (chart) {
        // Use a flag to check if the legend size has already been calculated
        if (chart._legendSizeCalculated) {
            return;
        }

        const calculateLegendSize = () => {
            const canvasHeight = chart.canvas.height;
            const canvasWidth = chart.canvas.width;

            // Calculate the font and box size based on canvas dimensions
            const fontSize = Math.max(Math.min(canvasHeight, canvasWidth) * 0.02, 8); // Min font size 8, max 2% of canvas size
            const boxSize = fontSize; // Box size equal to font size

            // Update legend label sizes if necessary
            if (
                chart.options.plugins.legend.labels.fontSize !== fontSize ||
                chart.options.plugins.legend.labels.boxWidth !== boxSize
            ) {
                chart.options.plugins.legend.labels.fontSize = fontSize;
                chart.options.plugins.legend.labels.boxWidth = boxSize;
                chart.options.plugins.legend.labels.boxHeight = boxSize; // Supported in Chart.js 3.7.0+

                // Mark that the legend size has been calculated to prevent infinite loop
                chart._legendSizeCalculated = true;

                chart.update();
            }
        };

        // Calculate initially
        calculateLegendSize();
    },
};

// Register the plugin
Chart.register(dynamicLegendHeightPlugin);


/**
 * @Description This function lets you return Dashboard page.
 * @Returns     Component
 * @Author      Emirhan Bilal Yeşil
 */

function Dashboard() {
    const [dashboardWidgets, setDashboardWidgets] = useState([])
    const [openConfigure, setOpenConfigure] = useState(false)
    const [openCreateDashboard, setOpenCreateDashboard] = useState(false)
    const [openCreateWidget, setOpenCreateWidget] = useState(false)
    const [selectedView, setSelectedView] = useState('Default');
    const [dateShowValue, setDateShowValue] = useState("weekly");
    const [customDateValue, setCustomDateValue] = useState({ start_date: "", end_date: "" });
    const [dashBoardTabs, setDashBoardTabs] = useState([]);
    const [selectedDashboard, setSelectedDashboard] = useState({});
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState({ open: false });
    const [openDeleteWidget, setOpenDeleteWidget] = useState({ open: false });
    const [loading, setLoading] = useState(false);
    const setAlert = useSetRecoilState(alertState);
    const intervalIdRef = useRef(null);
    const { t } = useTranslation();



    const label = 'CONTENT.DASHBOARD.';

    useEffect(() => {
        const fetchDashboards = async () => {
            const response = await DashBoardService.getDashboards()
            if (response.status) {
                setDashBoardTabs(response.data)
                const dashboardId = localStorage.getItem("dashboardTabId")
                const dashhboardTab = response.data.find((item) => item.id === dashboardId)
                if (dashhboardTab?.id) {
                    setSelectedDashboard(dashhboardTab)
                }
                else if (!selectedDashboard.id) {
                    setSelectedDashboard(response.data[0])
                }
            }
            else if (!response.status) {
                let message = t(label + response.message);
                if (response.message.includes("System Message:")) {
                    message = response.message.split("System Message: ")[1];
                }
                return setAlert(alert => ({
                    ...alert,
                    show: true,
                    message,
                    severity: 'error'
                }));
            }
        }
        fetchDashboards();
    }, [])

    useEffect(() => {
        if (selectedDashboard?.id) {
            fetchDashboardWidgets(true); // İlk veriyi hemen çek

            // Sadece interval yoksa oluştur
            if (!intervalIdRef.current) {
                intervalIdRef.current = setInterval(fetchDashboardWidgets, 1800000);
            }
        }

        if (intervalIdRef.current) {
            // Interval durdurulacaksa temizle
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }

        // Cleanup function
        return () => clearInterval(intervalIdRef.current);
    }, [selectedDashboard]);

    const fetchDashboardWidgets = async (isFirst) => {
        setLoading(true)
        const response = await DashBoardService.getDashboardById(selectedDashboard?.id)
        if (response.status) {
            // setDashboardWidgets((pre) => {
            //     let allWidgets = []
            //     if (response.data.length) {
            //         response.data.forEach(item => {
            //             allWidgets.push(identifyWidgets(item))
            //         });
            //     }
            //     return allWidgets
            // })
            if (response.data?.dashboard_widgets) {
                setDashboardWidgets(response.data.dashboard_widgets)
            }
            else if (!response.data?.dashboard_widgets) {
                setDashboardWidgets([])
            }
            if (isFirst) {
                if (response.data.date_type) {
                    setDateShowValue(response.data.date_type)
                }
                if (response.data.custom_date) {
                    setCustomDateValue(response.data.custom_date)
                }
            }
        }
        setLoading(false)
    }

    const handleRefresh = () => {
        fetchDashboardWidgets()
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null
        }
        intervalIdRef.current = setInterval(fetchDashboardWidgets, 1800000);
    }

    const updateDashboardDate = async (date) => {
        const body = {
            date_type: date ? date : dateShowValue,
            custom_date: {
                start_date: customDateValue.start_date,
                end_date: customDateValue.end_date,
            }
        }
        const response = await DashBoardService.updateDashboardDate(selectedDashboard?.id, body)
        if (response.status) {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null
            }
            await fetchDashboardWidgets(false)
            intervalIdRef.current = setInterval(fetchDashboardWidgets, 1800000);
        }
    }

    const moveWidget = async (draggedId, targetId, position) => {
        let updatedBoxes = JSON.parse(JSON.stringify(dashboardWidgets));

        const draggedIndex = updatedBoxes.findIndex((box) => box.id === draggedId);
        const targetIndex = updatedBoxes.findIndex((box) => box.id === targetId);

        const [removed] = updatedBoxes.splice(draggedIndex, 1);
        if (draggedIndex < targetIndex) {
            if (position === 'left') {
                updatedBoxes.splice(targetIndex - 1, 0, removed);
            } else {
                updatedBoxes.splice(targetIndex, 0, removed);
            }
        }
        else {
            if (position === 'left') {
                updatedBoxes.splice(targetIndex, 0, removed);
            } else {
                updatedBoxes.splice(targetIndex + 1, 0, removed);
            }
        }
        let updatedWidgets = []
        updatedBoxes.forEach((widget, index) => {
            widget.order = index + 1
            updatedWidgets.push({ id: widget.id, order: index + 1 })
        })
        setOpenConfigure(false)
        const response = await DashBoardService.updateWidgetOrders({ orders: updatedWidgets })
        if (response.status) {
            setDashboardWidgets(updatedBoxes);
        }
        setOpenConfigure(true)
    };

    // 15 saniyede bir getDashboardById

    const handleOpenConfigure = (status) => {
        setOpenConfigure(status)
        if (status) {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null
            }
        }
        else if (!status) {
            intervalIdRef.current = setInterval(fetchDashboardWidgets, 1800000);
        }
    }

    const handleCreateDashboard = () => {
        setOpenCreateDashboard(true)
    }
    const handleAddWidget = (id, order) => {
        if (!id) {
            setOpenCreateWidget({ show: true })
        }
        else {
            setOpenCreateWidget({ id: id, show: true, order: order })
        }
    }

    const handleOpenRemove = (status, data) => {
        if (data) {
            setOpenDeleteConfirm({ open: status, id: data.id, name: data.name })
            return
        }
        setOpenDeleteConfirm({ open: status })
    }

    const handleOpenRemoveWidget = (status, data) => {
        if (data) {
            setOpenDeleteWidget({ open: status, id: data.id, name: data.name })
            return
        }
        setOpenDeleteWidget({ open: status })
    }

    const handleDeleteWidget = async () => {
        let newWidgets = JSON.parse(JSON.stringify(dashboardWidgets))
        let updateWidgets = []
        let order = 1
        newWidgets = newWidgets.filter((widget) => {
            if (widget.id !== openDeleteWidget.id) {
                updateWidgets.push({ id: widget.id, order })
                order++
                return widget
            }
        })
        const response = await DashBoardService.deleteWidget(openDeleteWidget.id, { orders: updateWidgets })
        if (response.status) {
            setDashboardWidgets(newWidgets)
        }
        // [{ id: id, order: 1 }]
    }

    const handleRemoveDashboardTab = async () => {
        const response = await DashBoardService.deleteDashboard(openDeleteConfirm.id)
        if (response.status) {
            setDashBoardTabs((pre) => {
                let newDashboards = JSON.parse(JSON.stringify(pre))
                newDashboards = newDashboards.filter((dashboard) => dashboard.id !== openDeleteConfirm.id)
                if (selectedDashboard.id === openDeleteConfirm.id) {
                    setSelectedDashboard(newDashboards[0])
                }
                return newDashboards
            })
        }
    }

    return (
        <div className="db-cont">
            {/* <div className="db-header-contatiner"><QuickView /><Overview /></div> */}
            <div className="db-header-contatiner">
                <DashBoardHeader setOpenConfigure={setOpenConfigure} selectedView={selectedView}
                    setSelectedView={setSelectedView} dashBoardTabs={dashBoardTabs} handleCreateDashboard={handleCreateDashboard} setDateShowValue={setDateShowValue}
                    dateShowValue={dateShowValue} selectedDashboard={selectedDashboard} setSelectedDashboard={setSelectedDashboard} fetchDashboardWidgets={fetchDashboardWidgets}
                    handleOpenRemove={handleOpenRemove} openConfigure={openConfigure} customDateValue={customDateValue} setCustomDateValue={setCustomDateValue}
                    updateDashboardDate={updateDashboardDate} handleOpenConfigure={handleOpenConfigure} handleRefresh={handleRefresh} loading={loading} />
            </div>
            <div className="db-body-contatiner">
                {!loading ? <DashBoardBody dashboardWidgets={dashboardWidgets} label={label} handleOpenConfigure={handleOpenConfigure} handleAddWidget={handleAddWidget}
                    handleOpenRemoveWidget={handleOpenRemoveWidget} openConfigure={openConfigure} moveWidget={moveWidget}
                    dateShowValue={dateShowValue} customDateValue={customDateValue} /> : ""}
            </div>

            {/* {openConfigure ? <ConfigureWidggets handleClose={() => setOpenConfigure(false)} label={label} /> : ""} */}
            {/* {openConfigure ? <ConfigureLayout handleClose={() => setOpenConfigure(false)} label={label} /> : ""} */}
            {openCreateDashboard
                ? <CreateDashBoard handleClose={() => setOpenCreateDashboard(false)} dashBoardTabs={dashBoardTabs}
                    setDashBoardTabs={setDashBoardTabs} setSelectedDashboard={setSelectedDashboard} label={label} />
                : ""}
            {openCreateWidget.show ? <CreateWidget id={openCreateWidget?.id} handleClose={(refresh) => {
                setOpenCreateWidget({ id: null, show: false })
                if (refresh) {
                    fetchDashboardWidgets()
                }
            }} selectedDashboard={selectedDashboard} label={label} order={openCreateWidget?.order ? openCreateWidget?.order : dashboardWidgets.length + 1} /> : ""}
            {/* <div className="db-right-cont"><ActivityCenter /></div> */}
            {openDeleteConfirm.open && <NewDeleteConfirm titleMessage={t(label + "ABOUT-TO-DELETE", { tab: openDeleteConfirm.name })}
                deleteFunc={handleRemoveDashboardTab} setOpenDelete={handleOpenRemove} />}
            {openDeleteWidget.open && <NewDeleteConfirm titleMessage={t(label + "ABOUT-TO-DELETE-WIDGET", { widget: openDeleteConfirm.name })}
                deleteFunc={handleDeleteWidget} setOpenDelete={handleOpenRemoveWidget} />}
        </div>
    );
}
export default Dashboard;